// Module + sport definitions with role access (mirror of backend)
import {
  Activity, BookOpen, Briefcase, Calendar as Cal, ChartBar, ChevronRight, ClipboardCheck,
  DollarSign, Dumbbell, Flame, GraduationCap, HeartPulse, LayoutGrid, Megaphone,
  ShieldCheck, ShoppingBag, Trophy, Users, Wallet, BookCheck, Ticket, Sparkles,
  Building2, Wrench, Brain, Award, Video, Banknote, MapPin, Hammer, Utensils, ShieldAlert, Newspaper, MessageSquare, UserCog,
} from "lucide-react";
import { REVSHARE_VIEW_ROLES } from "./revshare";
import { RECRUITING_EXEC_ROLES } from "./recruitPipeline";

// Department modules grouped by category for sidebar organization
export const DEPARTMENT_GROUPS = [
  {
    label: "Overview",
    items: [
      { key: "dashboard", label: "Dashboard", path: "/app/dashboard", icon: LayoutGrid },
      { key: "athletes", label: "Athletes", path: "/app/athletes", icon: Users },
    ],
  },
  {
    label: "Academic Services",
    items: [
      { key: "academics", label: "Academics", path: "/app/academics", icon: BookOpen },
      { key: "compliance", label: "Compliance", path: "/app/compliance", icon: ShieldCheck },
      { key: "development", label: "S-A Development", path: "/app/development", icon: Award },
      { key: "wellness", label: "S-A Wellness", path: "/app/wellness", icon: Brain },
    ],
  },
  {
    label: "Performance",
    items: [
      { key: "medicine", label: "Sports Medicine", path: "/app/medicine", icon: HeartPulse },
      { key: "strength", label: "Strength & Cond.", path: "/app/strength", icon: Dumbbell },
      { key: "nutrition", label: "Nutrition", path: "/app/nutrition", icon: Utensils },
    ],
  },
  {
    label: "Operations",
    items: [
      { key: "event-ops", label: "Event Operations", path: "/app/operations", icon: ClipboardCheck },
      { key: "facilities", label: "Facilities & Grounds", path: "/app/facilities", icon: Building2 },
      { key: "equipment", label: "Equipment", path: "/app/equipment", icon: Hammer },
      { key: "business", label: "Business Office", path: "/app/business", icon: Banknote },
      { key: "security", label: "Security", path: "/app/security", icon: ShieldAlert },
    ],
  },
  {
    label: "Roster",
    items: [
      { key: "recruiting", label: "Recruiting Oversight", path: "/app/recruiting", icon: Flame },
    ],
  },
  {
    label: "Revenue",
    items: [
      { key: "nil", label: "NIL", path: "/app/nil", icon: DollarSign },
      { key: "ticketing", label: "Ticketing", path: "/app/ticketing", icon: Ticket },
      { key: "marketing", label: "Marketing & Fan XP", path: "/app/marketing", icon: Sparkles },
      { key: "fundraising", label: "Fundraising", path: "/app/fundraising", icon: Wallet },
    ],
  },
  {
    label: "Content",
    items: [
      { key: "communications", label: "Athletic Comms", path: "/app/communications", icon: Megaphone },
      { key: "sports-info", label: "Sports Information", path: "/app/sports-info", icon: Newspaper },
      { key: "video", label: "Video & Creative", path: "/app/video", icon: Video },
    ],
  },
];

// Flat list for backwards-compat where used
export const DEPARTMENT_MODULES = DEPARTMENT_GROUPS.flatMap((g) => g.items);

export const SHARED_MODULES = [
  { key: "messages", label: "Messages", path: "/app/messages", icon: MessageSquare },
  { key: "activity", label: "Activity", path: "/app/activity", icon: Activity },
  { key: "calendar", label: "Calendar", path: "/app/calendar", icon: Cal },
  { key: "tasks", label: "Tasks", path: "/app/tasks", icon: BookCheck },
  { key: "staff", label: "Staff Directory", path: "/app/staff", icon: GraduationCap },
  { key: "access", label: "Staff & Access", path: "/app/access", icon: UserCog },
  { key: "audit", label: "Audit Log", path: "/app/audit", icon: ShieldCheck },
];

export const SETTINGS_MODULE = { key: "settings", label: "School Settings", path: "/app/settings", icon: Trophy };

export const SPORTS = [
  "Football", "Men's Basketball", "Women's Basketball", "Baseball", "Softball",
  "Soccer", "Volleyball", "Track & Field", "Cross Country", "Tennis", "Golf",
];

// Sports that get the full tabbed/tree workspace (others are Overview-only).
export const RICH_SPORTS = ["football", "men's basketball", "women's basketball"];
export const isRichSport = (sport) => RICH_SPORTS.includes((sport || "").toLowerCase());

// URL slug  →  internal SportPage view key
export const SPORT_SLUG_TO_VIEW = {
  "": "overview",
  "roster": "roster",
  "team-stats": "team-stats",
  "roster-53": "roster-53",
  "roster-management": "roster-mgmt",
  "retention-board": "retention",
  "roster-forecast": "forecast",
  "projection-board": "projection",
  "depth-chart": "depth",
  "calendar": "calendar",
  "operations": "operations",
  "schedule": "schedule",
  "recruiting-board": "recruiting-board",
  "recruiting-dashboard": "recruiting-dashboard",
  "staff-scoreboard": "staff-scoreboard",
  "recruiting-coverage": "recruiting-coverage",
  "offer-sheets": "offer-sheets",
  "visits": "visits",
  "decision-board": "decision",
  "gm": "gm",
  "nil": "team-nil",
  "worksheets": "worksheets",
  "coaching-staff": "staff",
  "medical": "medical",
  "strength": "strength",
  "academics": "academics",
  "cara": "cara",
  "eligibility-timeline": "eligibility-timeline",
  "eligibility-board": "eligibility-board",
  "certifications": "certifications",
  "travel-ops": "travel-ops",
  "game-budget": "game-budget",
  "funding-board": "funding-board",
  "rev-share-planner": "rev-share",
  "aid-strategy": "aid-strategy",
  "aid-value": "aid-strategy",
  "aid-by-side": "aid-strategy",
  "high-schools": "high-schools",
  "report": "program-report",
  "team-comms": "team-comms",
  "player-intake": "player-intake",
};

const SPORT_NAV_BASE = [
  { label: null, items: [{ key: "overview", label: "Overview", slug: "" }] },
  { label: "Team", items: [
    { key: "roster", label: "Roster", slug: "roster" },
    { key: "depth", label: "Depth Chart", slug: "depth-chart" },
    { key: "staff", label: "Staff", slug: "coaching-staff" },
  ] },
  { label: "Operations", items: [
    { key: "schedule", label: "Game Schedule", slug: "schedule" },
    { key: "calendar", label: "Calendar", slug: "calendar" },
    { key: "operations", label: "Operations", slug: "operations" },
    { key: "travel-ops", label: "Travel / Ops", slug: "travel-ops" },
    { key: "game-budget", label: "Budget", slug: "game-budget" },
  ] },
  { label: "Front Office", items: [
    { key: "gm", label: "Executive Dashboard", slug: "gm" },
    { key: "team-nil", label: "Team NIL", slug: "nil" },
    { key: "funding-board", label: "Funding Board", slug: "funding-board" },
    { key: "aid-strategy", label: "Aid Strategy", slug: "aid-strategy" },
  ] },
];

// Grouped sub-navigation tree for a rich sport. Football uses the consolidated
// 8-hub information architecture; other rich sports keep the original grouping.
// NOTE: slugs are unchanged so every existing URL continues to resolve.
const RECRUITING_PIPELINE_TABS = [
  { key: "recruiting-dashboard", label: "Recruiting Dashboard", slug: "recruiting-dashboard", roles: RECRUITING_EXEC_ROLES },
  { key: "staff-scoreboard", label: "Staff Scoreboard", slug: "staff-scoreboard" },
  { key: "recruiting-coverage", label: "Coverage", slug: "recruiting-coverage" },
];
export function sportNavGroups(sport) {
  if ((sport || "").toLowerCase() === "football") {
    return [
      { key: "dashboard", label: "Dashboard", items: [
        { key: "overview", label: "Overview", slug: "" },
        { key: "gm", label: "Executive Dashboard", slug: "gm" },
      ] },
      { key: "roster-personnel", label: "Roster & Personnel", items: [
        { key: "roster", label: "Roster", slug: "roster" },
        { key: "depth", label: "Depth Chart", slug: "depth-chart" },
        { key: "staff", label: "Staff", slug: "coaching-staff" },
        { key: "roster-mgmt", label: "Roster Management", slug: "roster-management" },
        { key: "roster-53", label: "53-Man Roster", slug: "roster-53" },
        { key: "projection", label: "105 Projection", slug: "projection-board" },
        { key: "forecast", label: "Roster Forecast", slug: "roster-forecast" },
        { key: "retention", label: "Retention Board", slug: "retention-board" },
        { key: "team-stats", label: "Team Stats", slug: "team-stats" },
      ] },
      { key: "recruiting-intel", label: "Recruiting & Intel", items: [
        { key: "recruiting-board", label: "Recruiting Board", slug: "recruiting-board" },
        ...RECRUITING_PIPELINE_TABS,
        { key: "offer-sheets", label: "Offer Sheets", slug: "offer-sheets" },
        { key: "visits", label: "Visits", slug: "visits" },
        { key: "decision", label: "Decision Board", slug: "decision-board" },
        { key: "high-schools", label: "High Schools", slug: "high-schools" },
      ] },
      { key: "operations-schedule", label: "Operations & Schedule", items: [
        { key: "schedule", label: "Game Schedule", slug: "schedule" },
        { key: "calendar", label: "Calendar", slug: "calendar" },
        { key: "travel-ops", label: "Travel / Ops", slug: "travel-ops" },
        { key: "operations", label: "Operations", slug: "operations" },
        { key: "game-budget", label: "Budget", slug: "game-budget" },
      ] },
      { key: "academics-compliance", label: "Academics & Compliance", items: [
        { key: "academics", label: "Academics", slug: "academics", roles: ACADEMIC_TAB_ROLES },
        { key: "eligibility-timeline", label: "Eligibility Timeline", slug: "eligibility-timeline" },
        { key: "eligibility-board", label: "Eligibility & House", slug: "eligibility-board" },
        { key: "certifications", label: "Certifications", slug: "certifications" },
        { key: "cara", label: "Time Mgmt (CARA)", slug: "cara" },
      ] },
      { key: "performance-health", label: "Performance & Health", items: [
        { key: "medical", label: "Medical", slug: "medical", roles: MEDICAL_TAB_ROLES },
        { key: "strength", label: "Strength & Cond.", slug: "strength", roles: STRENGTH_TAB_ROLES },
      ] },
      { key: "front-office-nil", label: "Front Office & NIL", items: [
        { key: "team-nil", label: "Team NIL", slug: "nil" },
        { key: "funding-board", label: "Funding Board", slug: "funding-board" },
        { key: "rev-share", label: "Rev Share Planner", slug: "rev-share-planner", roles: REVSHARE_VIEW_ROLES },
        { key: "aid-strategy", label: "Aid Strategy", slug: "aid-strategy" },
        { key: "program-report", label: "Program Report", slug: "report" },
      ] },
      { key: "coaching", label: "Coaching", items: [
        { key: "worksheets", label: "Team Worksheets", slug: "worksheets" },
      ] },
      { key: "communication", label: "Communication", items: [
        { key: "team-comms", label: "Team Comms", slug: "team-comms" },
        { key: "player-intake", label: "Player Intake", slug: "player-intake" },
        { key: "messages", label: "Messages", slug: "messages", path: "/app/messages" },
      ] },
    ];
  }
  const groups = SPORT_NAV_BASE.map((g) => ({ ...g, items: [...g.items] }));
  groups.push({ label: "Intel", items: [
    { key: "recruiting-board", label: "Recruiting Board", slug: "recruiting-board" },
    ...RECRUITING_PIPELINE_TABS,
    { key: "offer-sheets", label: "Offer Sheets", slug: "offer-sheets" },
    { key: "visits", label: "Visits", slug: "visits" },
    { key: "decision", label: "Decision Board", slug: "decision-board" },
  ] });
  return groups;
}

export const MODULE_ACCESS = {
  dashboard: "*",
  athletes: "*",
  academics: ["athletic_director", "deputy_ad", "academic_advisor", "compliance"],
  compliance: ["athletic_director", "deputy_ad", "compliance"],
  development: "*",
  wellness: ["athletic_director", "deputy_ad", "academic_advisor", "trainer", "sports_medicine", "compliance"],
  medicine: ["athletic_director", "deputy_ad", "trainer", "sports_medicine", "strength_coach"],
  strength: ["athletic_director", "deputy_ad", "strength_coach", "trainer", "sports_medicine"],
  "event-ops": ["athletic_director", "deputy_ad", "operations", "equipment"],
  facilities: ["athletic_director", "deputy_ad", "operations", "equipment"],
  equipment: ["athletic_director", "deputy_ad", "operations", "equipment"],
  business: ["athletic_director", "deputy_ad"],
  nutrition: ["athletic_director", "deputy_ad", "trainer", "sports_medicine", "strength_coach"],
  security: ["athletic_director", "deputy_ad", "compliance", "head_coach", "gm", "operations"],
  gm: ["athletic_director", "deputy_ad", "gm", "head_coach"],
  recruiting: ["athletic_director", "deputy_ad", "recruiting_coordinator", "compliance"],
  nil: ["athletic_director", "deputy_ad", "nil", "compliance"],
  ticketing: ["athletic_director", "deputy_ad", "operations", "fundraising"],
  marketing: ["athletic_director", "deputy_ad", "communications", "fundraising"],
  fundraising: ["athletic_director", "deputy_ad", "fundraising"],
  communications: "*",
  "sports-info": ["athletic_director", "deputy_ad", "communications"],
  video: ["athletic_director", "deputy_ad", "communications"],
  calendar: "*",
  tasks: "*",
  staff: "*",
  messages: "*",
  access: ["athletic_director", "deputy_ad"],
  audit: ["athletic_director", "deputy_ad", "compliance"],
  settings: ["athletic_director", "deputy_ad"],
};

// Roles tied to a single sport — their sidebar is scoped to that sport, and the
// department-wide pages (run by department staff) are hidden from them.
export const SPORT_SCOPED_ROLES = ["gm", "head_coach", "assistant_coach", "offensive_coordinator", "defensive_coordinator"];

// Specialist roles — scoped to their assigned sport in the Sports nav, but they KEEP
// their own department page (Sports Medicine / Strength / Academics), which the backend
// read-wall filters to their sport.
export const SPORT_SPECIALIST_ROLES = ["trainer", "sports_medicine", "strength_coach", "academic_advisor"];

// Per-sport tab visibility (Football Health / Academic tabs). Medical excludes assistant coaches.
export const MEDICAL_TAB_ROLES = ["athletic_director", "deputy_ad", "head_coach", "gm", "trainer", "sports_medicine", "strength_coach"];
export const STRENGTH_TAB_ROLES = ["athletic_director", "deputy_ad", "head_coach", "assistant_coach", "offensive_coordinator", "defensive_coordinator", "gm", "strength_coach", "trainer", "sports_medicine"];
export const ACADEMIC_TAB_ROLES = ["athletic_director", "deputy_ad", "head_coach", "assistant_coach", "offensive_coordinator", "defensive_coordinator", "gm", "academic_advisor", "compliance"];
const SPORT_TAB_ROLES = { medical: MEDICAL_TAB_ROLES, strength: STRENGTH_TAB_ROLES, academics: ACADEMIC_TAB_ROLES, "rev-share": REVSHARE_VIEW_ROLES, "recruiting-dashboard": RECRUITING_EXEC_ROLES };
export function canAccessSportTab(viewKey, role) {
  if (role === "super_admin") return true;
  if (role === "chief_of_staff") role = "athletic_director"; // GM-level/above — sees everything
  const rule = SPORT_TAB_ROLES[viewKey];
  if (!rule) return true;
  return rule.includes(role);
}

// Student-athletes, parents and boosters only get Messages (the server denies everything else to them).
export const LOW_TRUST_ROLES = ["student_athlete", "parent", "booster"];
export const LOW_TRUST_MODULES = ["messages"];

export function canAccess(moduleKey, role) {
  if (role === "super_admin") return true;
  if (LOW_TRUST_ROLES.includes(role)) return LOW_TRUST_MODULES.includes(moduleKey);
  if (role === "chief_of_staff") role = "athletic_director"; // GM-level/above — sees everything
  const rule = MODULE_ACCESS[moduleKey];
  if (rule === "*" || !rule) return true;
  return rule.includes(role);
}

// ---- Plan entitlements (per-school packaging / SKUs) ----
export const PLANS = { full: "Full Athletic Dept", football_only: "Football Only" };
// A "Football Only" tenant sees: Football + department Compliance & Recruiting + Dashboard/Athletes,
// all shared essentials (Messages, Activity, Calendar, Tasks, Staff, Access, Audit), and Settings.
const FOOTBALL_ONLY_DEPT = new Set(["dashboard", "athletes", "compliance", "recruiting"]);
export function planAllowsModule(plan, key) {
  if (plan !== "football_only") return true;
  if (FOOTBALL_ONLY_DEPT.has(key)) return true;
  if (SHARED_MODULES.some((m) => m.key === key)) return true;
  return key === "settings" || key === "schools";
}
export function planAllowsSport(plan, sport) {
  if (plan !== "football_only") return true;
  return (sport || "").toLowerCase() === "football";
}
const _ALL_NAV_MODULES = [...DEPARTMENT_MODULES, ...SHARED_MODULES, SETTINGS_MODULE, { key: "schools", path: "/app/schools" }];
export function moduleKeyForPath(pathname) {
  const seg = pathname.replace(/^\/app\/?/, "").split("/")[0] || "";
  const base = "/app/" + seg;
  const m = _ALL_NAV_MODULES.find((x) => x.path === base);
  return m ? m.key : null;
}

export const ROLE_LABELS = {
  super_admin: "Super Admin",
  athletic_director: "Athletic Director",
  deputy_ad: "Deputy AD",
  gm: "Executive Dashboard",
  chief_of_staff: "Chief of Staff",
  head_coach: "Head Coach",
  assistant_coach: "Assistant Coach",
  offensive_coordinator: "Offensive Coordinator",
  defensive_coordinator: "Defensive Coordinator",
  recruiting_coordinator: "Recruiting Coordinator",
  academic_advisor: "Academic Advisor",
  compliance: "Compliance Officer",
  trainer: "Athletic Trainer",
  strength_coach: "Strength Coach",
  sports_medicine: "Sports Medicine",
  operations: "Operations",
  equipment: "Equipment",
  nil: "NIL Director",
  fundraising: "Development",
  communications: "Communications",
  student_athlete: "Student Athlete",
  parent: "Parent",
  booster: "Booster",
};

export const ICONS = {
  Activity, BookOpen, Briefcase, Cal, ChartBar, ChevronRight, ClipboardCheck,
  DollarSign, Dumbbell, Flame, GraduationCap, HeartPulse, LayoutGrid, Megaphone,
  ShieldCheck, ShoppingBag, Trophy, Users, Wallet, BookCheck, Ticket, Sparkles,
  Building2, Wrench, Brain, Award, Video, Banknote, MapPin, Hammer,
};
